<template>
  <div id="page-userIdentifyCard">
    <div class="page-wrapper">
      <div class="container">
        <div class="card">
          <!-- 프로필 이미지 섹션 -->
          <div class="profile-section">
            <div class="profile-image-wrapper">
              <img 
                v-if="userInfo.image"
                :src="userInfo.image" 
                alt="프로필 사진"
                class="profile-image"
              >
              <div v-else class="profile-image profile-placeholder">
                <i class="ph-fill ph-user"></i>
              </div>
            </div>
          </div>

          <!-- 이름 및 직급 -->
          <div class="name-section">
            <h1>{{ userInfo.name }}</h1>
            <span class="position">{{ userInfo.position || 'SM' }}</span>
          </div>

          <!-- 소개 문구 -->
          <p class="intro-text">
            {{ userInfo.intro || '보험전문 금융이슈팀닷넷 민플 공공받은 5%팁+입주호텔 5개기준 만보장잠실특급' }}
          </p>

          <!-- 전자명함 버튼 -->
          <button class="btn-primary" @click="shareCard">
            <i class="ph ph-share-network"></i>
            <span>전자명함 공유하기</span>
          </button>

          <!-- 소속 정보 -->
          <div class="info-section">
            <div class="section-title">소속 정보</div>
            <div class="info-list">
              <div class="info-item">
                <span class="label">지사</span>
                <span class="value">{{ userInfo.branch || 'VFT 필살지사' }}</span>
              </div>
              <div class="info-item">
                <span class="label">지점</span>
                <span class="value">{{ userInfo.team || 'AFPK CFP 우상인증설계사' }}</span>
              </div>
            </div>
          </div>

          <!-- QR 코드 -->
          <div class="qr-section">
            <div class="section-title">명함 QR 코드</div>
            <div class="qr-code-wrapper">
              <div class="qr-code" ref="qrCodeContainer"></div>
            </div>
            <p class="qr-description">QR 코드를 스캔하여 명함을 저장하세요</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { inject, ref, onMounted, nextTick } from "vue";
import { useRoute } from 'vue-router';
import QRCode from 'qrcode';

export default {
  name: 'UserIdentifyCard',
  setup() {
    const route = useRoute();
    const sql_query = inject('sql_query');
    
    const mb_id = ref('');
    const userInfo = ref({
      name: '',
      position: '',
      intro: '',
      branch: '',
      team: '',
      image: '',
      phone: '',
      email: ''
    });

    const qrCodeContainer = ref(null);

    onMounted(async () => {
      mb_id.value = route.params.mb_id;
      
      if (mb_id.value) {
        await loadUserInfo();
        await generateQRCode();
      }
    });

    async function loadUserInfo() {
      const sql = `SELECT * FROM member WHERE mb_id = '${mb_id.value}'`;
      const result = await sql_query(sql);
      
      if (result && result.length > 0) {
        const user = result[0];
        userInfo.value = {
          name: user.name || '설계사',
          position: user.position || 'SM',
          intro: user.intro || '보험 전문 상담을 도와드립니다.',
          branch: user.branch || 'VFT 지사',
          team: user.team || '영업팀',
          image: user.image || '',
          phone: user.phone || '',
          email: user.email || ''
        };
      }
    }

    async function generateQRCode() {
      await nextTick();
      
      if (qrCodeContainer.value) {
        // 기존 캔버스가 있다면 제거
        qrCodeContainer.value.innerHTML = '';
        
        //const cardUrl = `${window.location.origin}/event/register/${mb_id.value}`;
        const cardUrl = `https://amadiary.com?prf=${mb_id.value}`;

        // 캔버스 엘리먼트 생성
        const canvas = document.createElement('canvas');
        qrCodeContainer.value.appendChild(canvas);
        
        try {
          await QRCode.toCanvas(canvas, cardUrl, {
            width: 180,
            margin: 1,
            color: {
              dark: '#1a1a1a',
              light: '#ffffff'
            }
          });
        } catch (err) {
          console.error('QR 코드 생성 실패:', err);
        }
      }
    }

    async function shareCard() {
      const cardUrl = `${window.location.origin}/event/register/${mb_id.value}`;
      
      if (navigator.share) {
        try {
          await navigator.share({
            title: `${userInfo.value.name} 전자명함`,
            text: `${userInfo.value.name} ${userInfo.value.position}의 전자명함입니다.`,
            url: cardUrl
          });
        } catch (err) {
          console.log('공유 취소 또는 실패:', err);
        }
      } else {
        // Web Share API를 지원하지 않는 경우
        navigator.clipboard.writeText(cardUrl);
        alert('명함 링크가 복사되었습니다!');
      }
    }

    return {
      userInfo,
      qrCodeContainer,
      shareCard
    };
  }
}
</script>

<style scoped>
/* 전역 설정 */
#page-userIdentifyCard {
  width: 100%;
  min-height: 100vh;
  background: var(--color-background-light);
  position: relative;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  font-family: var(--font-family-base);
}

.page-wrapper {
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 20px 40px;
  box-sizing: border-box;
}

.container {
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  position: relative;
  animation: slideUp 0.8s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 카드 */
.card {
  background: var(--color-background);
  border-radius: 24px;
  padding: 48px 32px;
  box-shadow: var(--shadow-md);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  animation: cardFadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.2s backwards;
}

@keyframes cardFadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* 프로필 이미지 */
.profile-section {
  width: 100%;
  display: flex;
  justify-content: center;
  animation: profileSlide 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.3s backwards;
}

@keyframes profileSlide {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.profile-image-wrapper {
  position: relative;
  width: 140px;
  height: 140px;
}

.profile-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
  background: var(--color-background-light);
  box-shadow: var(--shadow-md);
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.profile-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-text-primary);
  color: var(--color-background);
  font-size: 56px;
}

.profile-image-wrapper:hover .profile-image {
  transform: scale(1.03);
}

/* 이름 섹션 */
.name-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  animation: fadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.4s backwards;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.name-section h1 {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  margin: 0;
  letter-spacing: -0.5px;
}

.position {
  font-size: var(--font-size-base);
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-medium);
  letter-spacing: -0.1px;
}

/* 소개 문구 */
.intro-text {
  font-size: var(--font-size-base);
  color: var(--color-text-secondary);
  text-align: center;
  line-height: 1.6;
  margin: 0;
  max-width: 100%;
  font-weight: var(--font-weight-normal);
  letter-spacing: -0.2px;
  animation: fadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.5s backwards;
}

/* 전자명함 버튼 */
.btn-primary {
  width: 100%;
  height: 52px;
  background: var(--color-special-point);
  border: none;
  border-radius: var(--radius-md);
  color: var(--color-text-white);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: var(--shadow-md);
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  letter-spacing: -0.2px;
  animation: fadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.6s backwards;
}

.btn-primary i {
  font-size: var(--font-size-lg);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px light-dark(rgba(0, 122, 255, 0.3), rgba(51, 170, 255, 0.4));
}

.btn-primary:active {
  transform: translateY(0);
  box-shadow: var(--shadow-sm);
}

/* 소속 정보 */
.info-section {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  animation: fadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.7s backwards;
}

.section-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-list {
  display: flex;
  flex-direction: column;
  gap: 1px;
  background: var(--color-background-light);
  border-radius: var(--radius-md);
  overflow: hidden;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: var(--color-background);
  transition: background 0.2s ease;
}

.info-item:first-child {
  border-radius: var(--radius-md) var(--radius-md) 0 0;
}

.info-item:last-child {
  border-radius: 0 0 var(--radius-md) var(--radius-md);
}

.info-item:only-child {
  border-radius: var(--radius-md);
}

.info-item .label {
  font-size: var(--font-size-base);
  color: var(--color-text-primary);
  font-weight: var(--font-weight-medium);
  letter-spacing: -0.2px;
}

.info-item .value {
  font-size: var(--font-size-base);
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-normal);
  text-align: right;
  letter-spacing: -0.2px;
  max-width: 60%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* QR 코드 */
.qr-section {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  animation: fadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.8s backwards;
}

.qr-code-wrapper {
  background: var(--color-background);
  padding: 20px;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--color-background-light);
}

.qr-code {
  width: 180px;
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-background);
}

.qr-code canvas {
  border-radius: var(--radius-md);
}

.qr-description {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  margin: 0;
  text-align: center;
  font-weight: var(--font-weight-normal);
  letter-spacing: -0.1px;
}

/* 모바일 최적화 */
@media (max-width: 768px) {
  .page-wrapper {
    padding: 40px 16px 32px;
  }

  .card {
    padding: 40px 24px;
    border-radius: 20px;
    gap: 28px;
  }

  .profile-image-wrapper {
    width: 120px;
    height: 120px;
  }

  .profile-placeholder {
    font-size: 48px;
  }

  .btn-primary {
    height: 48px;
  }

  .qr-code-wrapper {
    padding: 16px;
  }

  .qr-code {
    width: 160px;
    height: 160px;
  }
}

@media (max-width: 480px) {
  .page-wrapper {
    padding: 32px 12px 24px;
  }

  .card {
    padding: 32px 20px;
    gap: 24px;
  }

  .profile-image-wrapper {
    width: 110px;
    height: 110px;
  }

  .info-item {
    padding: 14px 16px;
  }
}

/* 스크롤바 스타일 (웹킷) */
::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: var(--color-text-secondary);
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: var(--color-text-primary);
}
</style>